import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { GrupoConocimiento } from 'src/app/domain/model/catalogo/grupo-conocimiento';
import { EmpleadoRequest } from 'src/app/domain/model/empleado/empleado';
import { YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { sanitizeText, sanitizeNumbersOnly } from 'src/app/common/input-utils';

/**
 * Dumb component: "Datos Consultor" de "Modificar Empleado/Consultor"
 * (HU1259115).
 *
 * <p>Legacy: `ModificarEmpleadoViewImpl.ui.xml` líneas 499-682 — orden
 * verificado campo a campo contra el `.ui.xml` (auditoría de fidelidad,
 * hallazgo A8): Grupo Conocimiento+Disponible Programación → Adscrito+Valor
 * Hora → Licencia S.O. (número+fecha) → Entidad S.O. → Líder+Transporte →
 * Rutas+Mixto → Higiene+Atención Masiva → datos de licencia de Higiene →
 * Pymes+Bloqueo Agenda. Perfil, Tipo Consultor, Programable, Tipo de
 * Facturación, Capacidad, Proyecto/Estrategia, Calcular Productividad,
 * Solución y los 2 municipios NO viven aquí — el legado los ubica en "Datos
 * de Persona" (ver `DatosPersonaEmpleadoComponent`, hallazgo A6).</p>
 *
 * <p>Visible solo cuando `esAsesor()` — el padre (`ModificarEmpleadoComponent`)
 * controla esa condición con `@if`, este componente no la repite.</p>
 */
@Component({
  selector: 'app-datos-consultor-empleado',
  standalone: true,
  imports: [CommonModule, FormsModule, FieldsetModule, InputTextModule, InputNumberModule, DropdownModule, RadioButtonModule, TooltipModule],
  template: `
    <p-fieldset legend="Datos Consultor" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Equipo Gestión Conocimiento</label>
          <p-dropdown [(ngModel)]="form().nmgrupoConocimiento" [options]="gruposConocimiento()"
            optionLabel="dsgrupo" optionValue="nmconsecutivo" placeholder="--Seleccione--"
            [filter]="true" filterBy="dsgrupo" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Grupo Conocimiento"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disponible Programación *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="dispProg" [value]="YES" [(ngModel)]="form().sndisponibleProgramacion" inputId="dispProgSi" pTooltip="Disponible para Programación"></p-radioButton>
              <label for="dispProgSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="dispProg" [value]="NO" [(ngModel)]="form().sndisponibleProgramacion" inputId="dispProgNo" pTooltip="Disponible para Programación"></p-radioButton>
              <label for="dispProgNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adscrito *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="adscrito" [value]="YES" [(ngModel)]="form().snadscrito" inputId="adscritoSi" pTooltip="Adscrito" (onClick)="cambioAdscrito.emit()"></p-radioButton>
              <label for="adscritoSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="adscrito" [value]="NO" [(ngModel)]="form().snadscrito" inputId="adscritoNo" pTooltip="Adscrito" (onClick)="cambioAdscrito.emit()"></p-radioButton>
              <label for="adscritoNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        @if (form().snadscrito === YES) {
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor Hora a Cobrar Adscrito *</label>
            <p-inputNumber [(ngModel)]="form().ptvalorCobrarAdscrito" mode="decimal" [minFractionDigits]="2"
              [maxFractionDigits]="2" [min]="0" [max]="99999999.99" styleClass="w-full" pTooltip="Valor Hora Adscrito"></p-inputNumber>
          </div>
        }

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número de Licencias de S.O.</label>
          <input pInputText [(ngModel)]="form().cdnumLicenciaSo" class="w-full" maxlength="20" pTooltip="Número Licencias S.O."
            (input)="form().cdnumLicenciaSo = sanitizeNumbersOnly(form().cdnumLicenciaSo)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Resolución Licencia S.O. (aaaa/mm/dd)</label>
          <input pInputText [(ngModel)]="form().feresolucionLicenciaSo" type="date" class="w-full" pTooltip="Fecha Resolución Licencia S.O." />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entidad que Expide S.O.</label>
          <input pInputText [(ngModel)]="form().dsentidadExpideLicSo" class="w-full" maxlength="50" pTooltip="Entidad que Expide S.O."
            (input)="form().dsentidadExpideLicSo = sanitizeText(form().dsentidadExpideLicSo)"
            (change)="form().dsentidadExpideLicSo = toUpperCase(form().dsentidadExpideLicSo)" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lider en Misión</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="lider" [value]="YES" [(ngModel)]="form().snlider" inputId="liderSi" pTooltip="Lider"></p-radioButton>
              <label for="liderSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="lider" [value]="NO" [(ngModel)]="form().snlider" inputId="liderNo" pTooltip="Lider"></p-radioButton>
              <label for="liderNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transporte *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="transporte" value="FIJO" [(ngModel)]="form().dstransporte" inputId="transporteFijo" pTooltip="Transporte"></p-radioButton>
              <label for="transporteFijo" class="ml-2 text-sm">Fijo</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="transporte" value="VARIABLE" [(ngModel)]="form().dstransporte" inputId="transporteVariable" pTooltip="Transporte"></p-radioButton>
              <label for="transporteVariable" class="ml-2 text-sm">Variable</label>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rutas *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="rutas" [value]="YES" [(ngModel)]="form().snruta" inputId="rutasSi" pTooltip="Rutas"></p-radioButton>
              <label for="rutasSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="rutas" [value]="NO" [(ngModel)]="form().snruta" inputId="rutasNo" pTooltip="Rutas"></p-radioButton>
              <label for="rutasNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mixto *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="mixto" [value]="YES" [(ngModel)]="form().snmixto" inputId="mixtoSi" pTooltip="Mixto"></p-radioButton>
              <label for="mixtoSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="mixto" [value]="NO" [(ngModel)]="form().snmixto" inputId="mixtoNo" pTooltip="Mixto"></p-radioButton>
              <label for="mixtoNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tiene Especialización en Higiene</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="especHigiene" [value]="YES" [(ngModel)]="form().snespecializacionHigiene" inputId="especHigieneSi" pTooltip="Tiene Especialización en Higiene" (onClick)="cambioEspecializacionHigiene.emit()"></p-radioButton>
              <label for="especHigieneSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="especHigiene" [value]="NO" [(ngModel)]="form().snespecializacionHigiene" inputId="especHigieneNo" pTooltip="Tiene Especialización en Higiene" (onClick)="cambioEspecializacionHigiene.emit()"></p-radioButton>
              <label for="especHigieneNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Atención masiva</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="atencionMasiva" [value]="YES" [(ngModel)]="form().snatencionMasiva" inputId="atencionMasivaSi"></p-radioButton>
              <label for="atencionMasivaSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="atencionMasiva" [value]="NO" [(ngModel)]="form().snatencionMasiva" inputId="atencionMasivaNo"></p-radioButton>
              <label for="atencionMasivaNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>

        @if (form().snespecializacionHigiene === YES) {
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número Licencia Higiene *</label>
            <input pInputText [(ngModel)]="form().cdnumLicenciaHigiene" class="w-full" maxlength="20" pTooltip="Número de Licencia en Higiene"
              (input)="form().cdnumLicenciaHigiene = sanitizeNumbersOnly(form().cdnumLicenciaHigiene)" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Resolución Licencia Higiene (aaaa/mm/dd) *</label>
            <input pInputText [(ngModel)]="form().feresolucionLicenciaHigiene" type="date" class="w-full" pTooltip="Fecha Resolución Licencia en Higiene" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entidad que Expide Higiene *</label>
            <input pInputText [(ngModel)]="form().dsentidadExpideLicHigiene" class="w-full" maxlength="50" pTooltip="Entidad que Expide Licenca en Higiene"
              (input)="form().dsentidadExpideLicHigiene = sanitizeText(form().dsentidadExpideLicHigiene)"
              (change)="form().dsentidadExpideLicHigiene = toUpperCase(form().dsentidadExpideLicHigiene)" />
          </div>
        }

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pymes *</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="pymes" [value]="YES" [(ngModel)]="form().snpymes" inputId="pymesSi" pTooltip="Pymes"></p-radioButton>
              <label for="pymesSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="pymes" [value]="NO" [(ngModel)]="form().snpymes" inputId="pymesNo" pTooltip="Pymes"></p-radioButton>
              <label for="pymesNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bloqueo agenda</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="bloqueoAgenda" [value]="YES" [(ngModel)]="form().snbloqueoAgenda" inputId="bloqueoAgendaSi"></p-radioButton>
              <label for="bloqueoAgendaSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="bloqueoAgenda" [value]="NO" [(ngModel)]="form().snbloqueoAgenda" inputId="bloqueoAgendaNo"></p-radioButton>
              <label for="bloqueoAgendaNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>
      </div>
    </p-fieldset>
  `,
})
export class DatosConsultorEmpleadoComponent {
  readonly form = input.required<EmpleadoRequest>();
  readonly gruposConocimiento = input.required<GrupoConocimiento[]>();

  /** Legacy: `addChangeHandlerSiAdscrito`/`addChangeHandlerNoAdscrito` — el facade decide si limpia el valor hora. */
  readonly cambioAdscrito = output<void>();
  /** Legacy: `addChangeHandlerSiEspecHigiene` — el facade decide si limpia los 3 campos de licencia. */
  readonly cambioEspecializacionHigiene = output<void>();

  protected readonly YES = YES_VALUE;
  protected readonly NO = NO_VALUE;
  protected readonly sanitizeText = sanitizeText;
  protected readonly sanitizeNumbersOnly = sanitizeNumbersOnly;

  /** Legacy: `Utilidades.getTextBoxChangeHandler(false)` — mayúsculas al salir del campo. */
  protected toUpperCase(valor: string | null | undefined): string {
    return (valor ?? '').toUpperCase();
  }
}
